// ─── Bootstrap Wizard ──────────────────────────────────────────
// Full blank-page project setup with intake-first dynamic paths.
// MANUS-WIZARD-V2 Task 3: IntakeScreen → ProjectStep → WizardShell (dynamic path).
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../api';
import type { WizardDnsStatus, WizardValidateResult, WizardStatusResponse, DnsRecord, ParsedSpec, WizardResumeResponse, PreValidateResult } from '../api';
import { ResumePrompt } from '../components/wizard/ResumePrompt';
import DeployVerifyStep from '../components/wizard/DeployVerifyStep';
import IntakeScreen from '../components/wizard/IntakeScreen';
import type { IntakeAnswers } from '../components/wizard/IntakeScreen';
import WizardShell from '../components/wizard/WizardShell';
import type { StepStatus as ShellStepStatus } from '../components/wizard/WizardShell';
import DeployConfigStep from '../components/wizard/DeployConfigStep';
import DeploymentTargetField from '../components/new-project/DeploymentTargetField';
import { CredentialField, FIELD_KEY_TO_HELP_ID } from '../components/credential-help';
import type { FieldId } from '../components/credential-help';
import { type DeploymentTargets, DEFAULT_TARGETS } from '../config/stack-deploy-map';
import { SpecAnalysisProgress } from '../components/SpecAnalysisProgress';
import { computeWizardPath, computeStepCount, computeStepNames, STEP_LABELS } from '../lib/path-matrix';
import type { WizardStep, DeployTarget } from '../lib/path-matrix';

type StepId = 'spec' | 'project' | 'ns-check' | 'github' | 'supabase' | 'cloudflare' | 'railway' | 'netlify' | 'mailgun' | 'stripe' | 'openai' | 'deploy-verify' | 'launch';

interface StepDef {
  id: StepId;
  label: string;
  tier: number;
  required: boolean;
}

const STEPS: StepDef[] = [
  { id: 'spec', label: 'What Are You Building?', tier: 0, required: false },
  { id: 'project', label: 'Project Setup', tier: 0, required: true },
  { id: 'ns-check', label: 'Nameserver Check', tier: 0, required: false },
  { id: 'github', label: 'GitHub', tier: 1, required: true },
  { id: 'supabase', label: 'Supabase', tier: 1, required: true },
  { id: 'cloudflare', label: 'Cloudflare DNS', tier: 2, required: false },
  { id: 'railway', label: 'Railway', tier: 3, required: false },
  { id: 'netlify', label: 'Netlify', tier: 4, required: false },
  { id: 'mailgun', label: 'Mailgun', tier: 5, required: false },
  { id: 'stripe', label: 'Stripe', tier: 6, required: false },
  { id: 'openai', label: 'OpenAI', tier: 7, required: false },
  { id: 'deploy-verify', label: 'Deploy & Verify', tier: 8, required: false },
  { id: 'launch', label: 'Launch', tier: 9, required: false },
];

type StepStatus = 'pending' | 'active' | 'complete' | 'failed' | 'skipped';

const TECH_STACK_OPTIONS = [
  { value: 'lumeniq-default', label: 'LumenIQ Default — Python/FastAPI + React/Vite/Tailwind' },
  { value: 'node-typescript', label: 'Node.js / TypeScript — Hono/Express + React/Vite/Tailwind' },
  { value: 'nextjs', label: 'Next.js — Next.js 14 + Tailwind' },
  { value: 'python-only', label: 'Python only — Python/FastAPI (API only)' },
  { value: 'other', label: 'Other (specify below)' },
];

// ─── Help tooltips per service ─────────────────────────────────
const SERVICE_HELP: Partial<Record<StepId, { title: string; steps: string[] }>> = {
  github: {
    title: 'How to create a GitHub Personal Access Token',
    steps: [
      'Go to github.com → Settings → Developer settings → Personal access tokens → Tokens (classic)',
      'Click "Generate new token (classic)"',
      'Give it a descriptive name (e.g. "OCNexus")',
      'Select scopes: repo (full control), read:org, workflow',
      'Click "Generate token" and copy the token immediately — it won\'t be shown again',
      'Paste the token (starts with ghp_) into the field above',
    ],
  },
  supabase: {
    title: 'Where to find your Supabase credentials',
    steps: [
      'Go to supabase.com → select your project',
      'Project URL: Settings → API → Project URL',
      'Service Role Key: Settings → API → service_role (secret) key',
      'The service role key bypasses Row Level Security — keep it secret',
    ],
  },
  cloudflare: {
    title: 'How to create a Cloudflare API Token',
    steps: [
      'Go to dash.cloudflare.com → My Profile → API Tokens',
      'Click "Create Token" → use "Edit zone DNS" template',
      'Zone Resources: select your domain',
      'Zone ID: found on your domain\'s Overview page (right sidebar)',
    ],
  },
  railway: {
    title: 'How to get a Railway API Token',
    steps: [
      'Go to railway.app → Account Settings → Tokens',
      'Click "Create Token" and copy it',
    ],
  },
  netlify: {
    title: 'How to get a Netlify Personal Access Token',
    steps: [
      'Go to app.netlify.com → User settings → Applications',
      'Under "Personal access tokens", click "New access token"',
      'Give it a name and copy the token',
    ],
  },
  mailgun: {
    title: 'Where to find your Mailgun API Key',
    steps: [
      'Go to app.mailgun.com → Settings → API Keys',
      'Copy your "Private API key" (starts with key-)',
    ],
  },
  stripe: {
    title: 'Where to find your Stripe Secret Key',
    steps: [
      'Go to dashboard.stripe.com → Developers → API keys',
      'Copy the "Secret key" (starts with sk_test_ or sk_live_)',
      'Use test mode keys for development',
    ],
  },
  openai: {
    title: 'Where to find your OpenAI API Key',
    steps: [
      'Go to platform.openai.com → API keys',
      'Click "Create new secret key" and copy it',
      'The key starts with sk-',
    ],
  },
};

// ─── Info Tooltip Component ────────────────────────────────────
function InfoTooltip({ serviceId }: { serviceId: StepId }) {
  const [open, setOpen] = useState(false);
  const help = SERVICE_HELP[serviceId];
  if (!help) return null;

  return (
    <span style={{ position: 'relative', display: 'inline-block', marginLeft: 6 }}>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(!open); }}
        style={{
          width: 18, height: 18, borderRadius: '50%',
          border: '1px solid var(--border)', background: open ? 'var(--orange)' : 'var(--surface-2)',
          color: open ? '#fff' : 'var(--text-dim)',
          fontSize: 11, fontWeight: 700, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          lineHeight: 1, padding: 0,
        }}
        aria-label="Help"
      >
        i
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 24, left: -8, zIndex: 100,
          width: 340, padding: 14,
          background: 'var(--bg-primary)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          fontSize: 12, lineHeight: 1.6,
        }}>
          <div style={{ fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>{help.title}</div>
          <ol style={{ margin: 0, paddingLeft: 18 }}>
            {help.steps.map((s, i) => (
              <li key={i} style={{ color: 'var(--text-dim)', marginBottom: 4 }}>{s}</li>
            ))}
          </ol>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); }}
            style={{
              marginTop: 8, fontSize: 11, padding: '3px 10px',
              border: '1px solid var(--border)', borderRadius: 4,
              background: 'var(--surface-2)', color: 'var(--text-dim)',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      )}
    </span>
  );
}

// ─── ServiceStep (extracted to stable component) ───────────────
function ServiceStep({ service, fields, helpText, wizardSessionId, stepStatuses, projectId, markStep, advanceStep, setAutomationLog, setCapabilities, setCodexStatus, setError }: {
  service: StepId;
  fields: Array<{ key: string; label: string; placeholder: string; type?: string }>;
  helpText?: string;
  wizardSessionId: string | null;
  stepStatuses: Record<StepId, StepStatus>;
  projectId: string | null;
  markStep: (id: StepId, status: StepStatus) => void;
  advanceStep: () => void;
  setAutomationLog: React.Dispatch<React.SetStateAction<string[]>>;
  setCapabilities: React.Dispatch<React.SetStateAction<string[]>>;
  setCodexStatus: React.Dispatch<React.SetStateAction<'not_started' | 'running' | 'complete' | 'failed'>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [openHelpId, setOpenHelpId] = useState<FieldId | null>(null);

  const handleToggleHelp = (fieldId: FieldId) => {
    setOpenHelpId(prev => prev === fieldId ? null : fieldId);
  };
  const [result, setResult] = useState<WizardValidateResult | null>(null);
  const [pendingDnsRecords, setPendingDnsRecords] = useState<DnsRecord[]>([]);
  const [preValidateResult, setPreValidateResult] = useState<PreValidateResult | null>(null);
  const [preValidating, setPreValidating] = useState(false);

  // Pre-validate credentials before full validation (lightweight check)
  const handlePreValidate = async () => {
    const creds: Record<string, string> = {};
    for (const f of fields) {
      if (values[f.key]) creds[f.key] = values[f.key];
    }
    // Only pre-validate if at least one credential field has a value
    if (Object.keys(creds).length === 0) return;
    setPreValidating(true);
    setPreValidateResult(null);
    try {
      const res = await api.wizardPreValidate(service, creds);
      setPreValidateResult(res);
    } catch {
      // Pre-validate is best-effort — don't block the user
      setPreValidateResult(null);
    } finally {
      setPreValidating(false);
    }
  };

  const handleValidate = async () => {
    if (!wizardSessionId) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const payload: { wizardSessionId: string; service: string; [key: string]: unknown } = { wizardSessionId, service };
      for (const f of fields) payload[f.key] = values[f.key] || '';
      const res = await api.wizardValidate(payload);
      setResult(res);
      if (res.automationLog?.length) {
        setAutomationLog(prev => [...prev, ...res.automationLog]);
      }
      if (res.dnsRecords?.length && !res.dnsRecords.every((r: DnsRecord) => r.written)) {
        setPendingDnsRecords(res.dnsRecords.filter((r: DnsRecord) => !r.written));
      }
      if (res.valid) {
        if (res.identity) setCapabilities(prev => [...prev, `${STEPS.find(s => s.id === service)?.label} — ${res.identity}`]);
        // Show next-step guidance from API if available
        if (res.nextStepHint) {
          setAutomationLog(prev => [...prev, `→ ${res.nextStepHint}`]);
        }
        if (res.repoUrl) {
          setAutomationLog(prev => [...prev, `✓ Repo: ${res.repoUrl}`]);
        }
        markStep(service, 'complete');
        // Auto-kickoff after GitHub + Supabase
        if (service === 'github' || service === 'supabase') {
          const otherService = service === 'github' ? 'supabase' : 'github';
          if (stepStatuses[otherService] === 'complete' && projectId && wizardSessionId) {
            setAutomationLog(prev => [...prev, '⚡ GitHub + Supabase confirmed — starting Codex scaffold…']);
            setCodexStatus('running');
            api.wizardKickoff({ wizardSessionId, projectId }).then(res => {
              setAutomationLog(prev => [...prev, `✓ Codex started — run ${res.runId}`]);
            }).catch(() => {
              setCodexStatus('failed');
            });
          }
        }
        // Delay advance slightly to let the user see the success state
        setTimeout(() => advanceStep(), 600);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Validation failed');
    } finally {
      setLoading(false);
    }
  };

  const step = STEPS.find(s => s.id === service)!;

  return (
    <div className="wizard-step-content">
      <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 600 }}>{step.label}</h2>
      {helpText && <p style={{ color: 'var(--text-dim)', fontSize: 13, margin: '0 0 16px' }}>{helpText}</p>}
      {fields.map(f => {
        const helpId = FIELD_KEY_TO_HELP_ID[`${service}:${f.key}`];
        if (helpId) {
          return (
            <CredentialField
              key={f.key}
              fieldId={helpId}
              label={f.label}
              value={values[f.key] || ''}
              onChange={v => { setValues(prev => ({ ...prev, [f.key]: v })); }}
              placeholder={f.placeholder}
              isSecret={f.type === 'password'}
              openHelpId={openHelpId}
              onToggleHelp={handleToggleHelp}
            />
          );
        }
        // Fallback for fields without help content
        return (
          <div key={f.key} className="wizard-field">
            <label style={{ display: 'flex', alignItems: 'center' }}>
              {f.label}
              <InfoTooltip serviceId={service} />
            </label>
            <input
              type={f.type || 'text'}
              value={values[f.key] || ''}
              onChange={e => setValues(prev => ({ ...prev, [f.key]: e.target.value }))}
              onBlur={handlePreValidate}
              placeholder={f.placeholder}
            />
          </div>
        );
      })}
      {/* Pre-validate feedback (shown before full validation) */}
      {preValidateResult && !result && (
        <div style={{
          marginTop: 8,
          padding: '8px 12px',
          borderRadius: 6,
          fontSize: 12,
          lineHeight: 1.5,
          background: preValidateResult.valid ? 'var(--bg-success, #f0fdf4)' : 'var(--bg-warning, #fffbeb)',
          border: `1px solid ${preValidateResult.valid ? 'var(--border-success, #bbf7d0)' : 'var(--border-warning, #fde68a)'}`,
          color: 'var(--text-primary)',
        }}>
          {preValidating && <span style={{ color: 'var(--text-dim)' }}>Checking credentials...</span>}
          {!preValidating && preValidateResult.valid && (
            <span style={{ color: 'var(--text-success, #16a34a)' }}>
              \u2713 Credentials look valid ({preValidateResult.checks.filter(c => c.passed).length}/{preValidateResult.checks.length} checks passed)
              {preValidateResult.scopes && preValidateResult.scopes.length > 0 && (
                <span style={{ marginLeft: 8, color: 'var(--text-dim)' }}>Scopes: {preValidateResult.scopes.join(', ')}</span>
              )}
            </span>
          )}
          {!preValidating && !preValidateResult.valid && (
            <span style={{ color: 'var(--text-warning, #d97706)' }}>
              \u26a0 {preValidateResult.errors[0] || 'Credential check failed'}
              {preValidateResult.checks.filter(c => !c.passed && c.suggestion).map((c, i) => (
                <div key={i} style={{ marginTop: 4, fontSize: 11, color: 'var(--text-dim)' }}>
                  \u2192 {c.suggestion}
                </div>
              ))}
            </span>
          )}
          {preValidateResult.warnings.length > 0 && (
            <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-dim)' }}>
              {preValidateResult.warnings.map((w, i) => <div key={i}>\u26a0 {w}</div>)}
            </div>
          )}
        </div>
      )}
      {result && !result.valid && (
        <div className="wizard-error-box">
          {result.error}
          {(result as any).error_type && (
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-dim)' }}>
              {(result as any).error_type === 'token_expired' && '\u2139\ufe0f Your token is expired or invalid. Please generate a new one and try again.'}
              {(result as any).error_type === 'token_revoked' && '\u2139\ufe0f This token has been revoked. Generate a new token from your Railway dashboard.'}
              {(result as any).error_type === 'rate_limited' && '\u23f3 Railway rate limit reached. Wait a moment and try again.'}
              {(result as any).error_type === 'insufficient_scope' && '\u2139\ufe0f Your token lacks required permissions. Generate a new token with full access scope.'}
              {(result as any).error_type === 'network_error' && '\ud83c\udf10 Cannot reach Railway API. Check your network connection and try again.'}
              {(result as any).error_type === 'server_error' && '\u26a0\ufe0f Railway is experiencing issues. This is temporary \u2014 try again in a few seconds.'}
            </div>
          )}
          {(result as any).requiresNewToken && (
            <button
              className="wizard-btn secondary"
              style={{ marginTop: 8, fontSize: 12 }}
              onClick={() => { setResult(null); setValues({}); }}
            >
              Re-enter Token
            </button>
          )}
        </div>
      )}
      {result?.valid && (
        <div className="wizard-success-box">✓ Connected{result.identity ? ` — ${result.identity}` : ''} — advancing…</div>
      )}
      {/* DNS Fallback Table — shown when records need manual creation (no Cloudflare) */}
      {pendingDnsRecords.length > 0 && (
        <div style={{ marginTop: 16, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 16 }}>
          <h4 style={{ fontSize: 13, fontWeight: 600, margin: '0 0 4px', color: 'var(--text-primary)' }}>⚠ Manual DNS Records Required</h4>
          <p style={{ fontSize: 12, color: 'var(--text-dim)', margin: '0 0 12px', lineHeight: 1.5 }}>
            Cloudflare is not configured for this project. Add these DNS records to your provider manually.
            Each row has a copy button to copy the full record to your clipboard.
          </p>
          <table className="ocn-grid">
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th className="nowrap" style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Type</th>
                <th className="nowrap" style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Name</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Value</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Purpose</th>
                <th className="nowrap" style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 600, width: 60 }}>Copy</th>
              </tr>
            </thead>
            <tbody>
              {pendingDnsRecords.map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td className="nowrap" style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.type}</td>
                  <td className="nowrap" style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{r.name}</td>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace', wordBreak: 'break-all', maxWidth: 240 }}>{r.content}</td>
                  <td style={{ padding: '6px 8px', fontSize: 11, color: 'var(--text-dim)' }}>{r.purpose ?? '—'}</td>
                  <td className="nowrap" style={{ padding: '6px 8px', textAlign: 'center' }}>
                    <button
                      style={{ fontSize: 11, padding: '2px 8px', cursor: 'pointer', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                      onClick={() => navigator.clipboard.writeText(`${r.type}\t${r.name}\t${r.content}${r.priority ? `\t${r.priority}` : ''}`)}
                    >📋</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button className="wizard-btn primary" onClick={handleValidate} disabled={loading || (result?.valid === true)}>
          {loading ? 'Validating…' : result?.valid ? '✓ Connected' : 'Connect'}
        </button>
        {!step.required && (
          <button className="wizard-btn secondary" onClick={() => { markStep(service, 'skipped'); advanceStep(); }}>
            Skip
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Main Wizard Component ─────────────────────────────────────
interface WizardPageProps {
  onComplete?: () => void;
}

export default function WizardPage({ onComplete }: WizardPageProps) {
  // ─── Wizard V2: Phase management ─────────────────────────
  // Phase flow: 'intake' → 'project' → 'wizard' (dynamic path)
  type WizardPhase = 'intake' | 'project' | 'wizard';
  const [wizardPhase, setWizardPhase] = useState<WizardPhase>('intake');
  const [intakeAnswers, setIntakeAnswers] = useState<IntakeAnswers | null>(null);

  // Computed wizard path from intake answers (memoised)
  const wizardPath = useMemo<WizardStep[]>(
    () => intakeAnswers ? computeWizardPath(intakeAnswers) : [],
    [intakeAnswers],
  );

  // ─── Existing state (unchanged) ──────────────────────────
  const [currentStep, setCurrentStep] = useState(0);
  const [stepStatuses, setStepStatuses] = useState<Record<StepId, StepStatus>>(
    () => Object.fromEntries(STEPS.map((s, i) => [s.id, i === 0 ? 'active' : 'pending'])) as Record<StepId, StepStatus>
  );
  const [wizardSessionId, setWizardSessionId] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [dnsStatus, setDnsStatus] = useState<WizardDnsStatus | null>(null);
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [automationLog, setAutomationLog] = useState<string[]>([]);
  const [codexStatus, setCodexStatus] = useState<'not_started' | 'running' | 'complete' | 'failed'>('not_started');
  const [error, setError] = useState<string | null>(null);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [resumeProjectName, setResumeProjectName] = useState<string>('');
  const logRef = useRef<HTMLDivElement>(null);

  // Auto-scroll automation log
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [automationLog]);

  // Resume wizard from saved state (DB-backed with sessionStorage fallback)
  useEffect(() => {
    const saved = sessionStorage.getItem('wizard_session');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        const pid = data.projectId;
        // Restore intake answers from sessionStorage if available
        if (data.intakeAnswers) {
          setIntakeAnswers(data.intakeAnswers as IntakeAnswers);
        }
        if (pid) {
          // Try DB-backed resume first
          api.wizardResume(pid).then((resume: WizardResumeResponse) => {
            if (resume.completed) {
              sessionStorage.removeItem('wizard_session');
              onComplete?.();
              return;
            }
            if (resume.wizardSessionId) {
              setWizardSessionId(resume.wizardSessionId);
              setProjectId(resume.projectId);
              if (resume.unlockedCapabilities) setCapabilities(resume.unlockedCapabilities);
              if (resume.automationLog) setAutomationLog(resume.automationLog);
              if (resume.codexStatus) setCodexStatus(resume.codexStatus);
              if (resume.dnsStatus) setDnsStatus(resume.dnsStatus);
              // Restore step statuses
              const newStatuses = { ...stepStatuses };
              let firstIncomplete = resume.wizardStep ?? 0;
              if (resume.steps) {
                for (const [svc, st] of Object.entries(resume.steps)) {
                  if (svc in newStatuses) {
                    (newStatuses as Record<string, StepStatus>)[svc] = st === 'complete' ? 'complete' : st === 'failed' ? 'failed' : st === 'skipped' ? 'skipped' : 'pending';
                  }
                }
                // Find the first non-complete step
                for (let i = 0; i < STEPS.length; i++) {
                  if (newStatuses[STEPS[i].id] !== 'complete' && newStatuses[STEPS[i].id] !== 'skipped') {
                    firstIncomplete = i;
                    newStatuses[STEPS[i].id] = 'active';
                    break;
                  }
                }
              }
              setStepStatuses(newStatuses);
              setCurrentStep(firstIncomplete);
              // Skip intake — resume directly into wizard phase
              setWizardPhase('wizard');

              // W-7: Show resume prompt if at least 1 step complete AND at least 1 incomplete
              const completedCount = Object.values(newStatuses).filter(s => s === 'complete' || s === 'skipped').length;
              const hasIncomplete = Object.values(newStatuses).some(s => s !== 'complete' && s !== 'skipped');
              if (completedCount > 0 && hasIncomplete) {
                setResumeProjectName(resume.displayName || resume.domain || resume.projectId || pid);
                setShowResumePrompt(true);
              }
            }
          }).catch(() => {
            // Fallback to sessionStorage-based resume
            setWizardSessionId(data.wizardSessionId);
            setProjectId(data.projectId);
            if (data.wizardSessionId) {
              api.wizardStatus(data.wizardSessionId).then((status: WizardStatusResponse) => {
                setCapabilities(status.unlockedCapabilities);
                setAutomationLog(status.automationLog);
                setCodexStatus(status.codexStatus);
                setDnsStatus(status.dnsStatus);
                const newStatuses = { ...stepStatuses };
                let firstIncomplete = STEPS.length - 1;
                for (const [svc, st] of Object.entries(status.steps)) {
                  if (svc in newStatuses) {
                    (newStatuses as Record<string, StepStatus>)[svc] = st === 'complete' ? 'complete' : st === 'failed' ? 'failed' : 'pending';
                  }
                }
                for (let i = 0; i < STEPS.length; i++) {
                  if (newStatuses[STEPS[i].id] !== 'complete' && newStatuses[STEPS[i].id] !== 'skipped') {
                    firstIncomplete = i;
                    newStatuses[STEPS[i].id] = 'active';
                    break;
                  }
                }
                setStepStatuses(newStatuses);
                setCurrentStep(firstIncomplete);
                // Skip intake — resume directly into wizard phase
                setWizardPhase('wizard');

                // W-7: Show resume prompt (sessionStorage fallback path)
                const completedCount2 = Object.values(newStatuses).filter(s => s === 'complete' || s === 'skipped').length;
                const hasIncomplete2 = Object.values(newStatuses).some(s => s !== 'complete' && s !== 'skipped');
                if (completedCount2 > 0 && hasIncomplete2) {
                  setResumeProjectName(data.projectName || data.projectId || '');
                  setShowResumePrompt(true);
                }
              }).catch(() => {});
            }
          });
        }
      } catch { /* ignore */ }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markStep = useCallback((id: StepId, status: StepStatus) => {
    setStepStatuses(prev => ({ ...prev, [id]: status }));
  }, []);

  const advanceStep = useCallback(() => {
    setCurrentStep(prev => {
      const next = prev + 1;
      if (next < STEPS.length) {
        // Use setTimeout to avoid nested state updates
        setTimeout(() => {
          setStepStatuses(s => ({ ...s, [STEPS[next].id]: 'active' }));
        }, 0);
      }
      // Persist step to DB (fire-and-forget)
      if (wizardSessionId) {
        api.wizardPersistStep(wizardSessionId, next).catch(() => {});
      }
      return next;
    });
  }, [wizardSessionId]);

  // ─── Step 0: Project Setup ────────────────────────────────
  const ProjectStep = () => {
    const [name, setName] = useState('');
    const [desc, setDesc] = useState('');
    const [stack, setStack] = useState('');
    const [target, setTarget] = useState<DeploymentTargets>({ ...DEFAULT_TARGETS });
    const [domain, setDomain] = useState('');
    const [loading, setLoading] = useState(false);
    const [specFile, setSpecFile] = useState<File | null>(null);
    const [showCustomStack, setShowCustomStack] = useState(false);
    const [parsing, setParsing] = useState(false);
    const [parsedSpec, setParsedSpec] = useState<ParsedSpec | null>(null);
    const [parseError, setParseError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);

    const handleSpecUpload = async (file: File) => {
      setSpecFile(file);
      setParsing(true);
      setParseError(null);
      setParsedSpec(null);
      try {
        const result = await api.wizardParseSpec(file);
        setParsedSpec(result);
        setLiftedParsedSpec(result); // Share with SpecStep for auto-save
        // Pre-fill fields from parsed spec
        if (result.parsed.displayName && !name) setName(result.parsed.displayName);
        if (result.parsed.description && !desc) setDesc(result.parsed.description);
        if (result.parsed.techStack && !stack) setStack(result.parsed.techStack);
        // Legacy: if spec parser returns a string deploymentTarget, try to map it
        if (result.parsed.deploymentTarget && target.frontend === DEFAULT_TARGETS.frontend) {
          // Keep structured targets — spec parsing doesn't override the multi-layer config
        }
        if (result.parsed.domain && !domain) setDomain(result.parsed.domain);
        setAutomationLog(prev => [...prev, `✓ Spec parsed: ${file.name} (${result.extractedChars.toLocaleString()} chars extracted)`]);
      } catch (err: unknown) {
        setParseError(err instanceof Error ? err.message : 'Failed to parse spec');
      } finally {
        setParsing(false);
      }
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleSpecUpload(file);
    };

    const handleSubmit = async () => {
      if (!name.trim()) { setError('Project name is required'); return; }
      setLoading(true);
      setError(null);
      try {
        const result = await api.wizardStart({
          name: name.trim(),
          description: desc,
          techStack: stack,
          deploymentTarget: JSON.stringify(target),
          domain: domain || undefined,
          project_type: intakeAnswers?.projectType,
          wizard_path: wizardPath.length > 0 ? wizardPath : undefined,
        });
        setWizardSessionId(result.wizardSessionId);
        setProjectId(result.projectId);
        setDnsStatus(result.dnsStatus);
        // Persist session + intake answers for resume
        sessionStorage.setItem('wizard_session', JSON.stringify({
          wizardSessionId: result.wizardSessionId,
          projectId: result.projectId,
          intakeAnswers,
        }));
        setAutomationLog(prev => [...prev, `✓ Project "${name}" created (${result.projectId})`]);
        // Auto-save SPEC.md if a spec was parsed during SpecStep (deferred because session didn't exist yet)
        if (liftedParsedSpec) {
          const p = liftedParsedSpec.parsed;
          const specContent = `# Project Specification\n\n${p.summary || ''}\n\n## Problem\n${p.problemStatement || p.description || ''}\n\n## Target Users\n${p.targetUsers || ''}\n\n## Core Features\n${(p.keyFeatures || []).map((f: string) => `- ${f}`).join('\n')}\n\n## Tech Stack\n${p.techPreferences || p.techStack || ''}\n\n## Success Criteria\n${p.successCriteria || ''}\n`;
          api.wizardSaveSpec(result.wizardSessionId, specContent)
            .then(() => setAutomationLog(prev => [...prev, `✓ SPEC.md saved from uploaded document`]))
            .catch(() => { /* non-blocking — spec can be re-uploaded later */ });
        }
        markStep('project', 'complete');
        // Transition to dynamic wizard phase (step 0 = first step in wizardPath)
        setCurrentStep(0);
        setWizardPhase('wizard');
        // Mark the first dynamic step as active
        if (wizardPath.length > 0) {
          const firstDynamic = wizardPath[0];
          const legacyFirst = firstDynamic === 'deploy_verify' ? 'deploy-verify' : firstDynamic === 'deploy_config' ? 'railway' : firstDynamic;
          setTimeout(() => setStepStatuses(s => ({ ...s, [legacyFirst]: 'active' })), 0);
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to create project');
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="wizard-step-content">
        <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 600 }}>New Project</h2>
        <p style={{ color: 'var(--text-dim)', fontSize: 13, margin: '0 0 20px' }}>Configure your project basics. GitHub + Supabase are required; everything else is optional.</p>

        {/* Monorepo beta notice */}
        <div style={{
          background: 'rgba(56, 152, 236, 0.08)', border: '1px solid rgba(56, 152, 236, 0.2)',
          borderRadius: 8, padding: '10px 14px', marginBottom: 16,
          display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: 'var(--text-dim)',
        }}>
          <span style={{ fontSize: 14, lineHeight: 1.4 }}>&#8505;&#65039;</span>
          <span>Beta expects a monorepo &mdash; frontend + API in one repo works best. Multi-repo support coming soon.</span>
        </div>

        {/* Spec Upload Zone */}
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? 'var(--orange)' : parsedSpec ? 'var(--green)' : 'var(--border)'}`,
            borderRadius: 10,
            padding: '20px 24px',
            marginBottom: 20,
            textAlign: 'center',
            cursor: 'pointer',
            background: dragOver ? 'rgba(255,160,0,0.06)' : parsedSpec ? 'rgba(0,200,100,0.04)' : 'var(--surface-2)',
            transition: 'all 0.2s ease',
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.md,.txt,.doc"
            style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; if (f) handleSpecUpload(f); }}
          />
          {parsing ? (
            <SpecAnalysisProgress filename={specFile?.name} />
          ) : parsedSpec ? (
            <div>
              <div style={{ fontSize: 20, marginBottom: 6 }}>✓</div>
              <div style={{ fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>Spec parsed — fields pre-filled</div>
              <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 4 }}>{specFile?.name} · {parsedSpec.extractedChars.toLocaleString()} chars</div>
              <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>Drop another file to re-analyze</div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 20, marginBottom: 6 }}>📄</div>
              <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>Upload a Project Spec</div>
              <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 4 }}>Drop a PDF, DOCX, or Markdown file here — AI will extract project details and pre-fill the form</div>
            </div>
          )}
        </div>

        {parseError && (
          <div className="wizard-error-box" style={{ marginBottom: 16, fontSize: 12 }}>
            Spec parsing failed: {parseError}
            <button onClick={() => setParseError(null)} style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>✕</button>
          </div>
        )}

        {/* Parsed Spec Summary */}
        {parsedSpec?.parsed.summary && (
          <div style={{
            background: 'var(--surface-2)', border: '1px solid var(--border)',
            borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 12, lineHeight: 1.6,
          }}>
            <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--text)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>AI Summary</div>
            <div style={{ color: 'var(--text-dim)' }}>{parsedSpec.parsed.summary}</div>
            {parsedSpec.parsed.keyFeatures && parsedSpec.parsed.keyFeatures.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Key Features</div>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {parsedSpec.parsed.keyFeatures.map((f, i) => (
                    <li key={i} style={{ color: 'var(--text-dim)', marginBottom: 2 }}>{f}</li>
                  ))}
                </ul>
              </div>
            )}
            {parsedSpec.parsed.services && Object.entries(parsedSpec.parsed.services).some(([, v]) => v) && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Detected Services</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {Object.entries(parsedSpec.parsed.services).filter(([, v]) => v).map(([svc]) => (
                    <span key={svc} style={{
                      background: 'var(--orange)', color: '#000', fontSize: 11,
                      padding: '2px 8px', borderRadius: 4, fontWeight: 600, textTransform: 'capitalize',
                    }}>{svc}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="wizard-field">
          <label>Project Name *</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="my-saas-app" autoFocus />
        </div>
        <div className="wizard-field">
          <label>Description</label>
          <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="A brief description of your project" />
        </div>
        <div className="wizard-field">
          <label>Tech Stack</label>
          <select
            value={TECH_STACK_OPTIONS.some(o => o.value === stack) ? stack : (stack ? 'other' : '')}
            onChange={e => {
              const v = e.target.value;
              if (v === 'other') { setStack(''); setShowCustomStack(true); }
              else { setStack(v); setShowCustomStack(false); }
            }}
            style={{ width: '100%', padding: '8px 12px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 13 }}
          >
            <option value="" disabled>Select a tech stack…</option>
            {TECH_STACK_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {showCustomStack && (
            <input
              value={stack}
              onChange={e => setStack(e.target.value)}
              placeholder="Describe your tech stack…"
              style={{ marginTop: 8 }}
              autoFocus
            />
          )}
        </div>
        <DeploymentTargetField
          stack={stack}
          value={target}
          onChange={setTarget}
        />
        <div className="wizard-field">
          <label>Domain (optional)</label>
          <input value={domain} onChange={e => setDomain(e.target.value)} placeholder="example.com" />
        </div>
        <button className="wizard-btn primary" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Creating…' : 'Create Project'}
        </button>
      </div>
    );
  };

  // ─── Step 1: Nameserver Check ─────────────────────────────
  const NsCheckStep = () => {
    const [polling, setPolling] = useState(false);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const pollDns = useCallback(async () => {
      if (!wizardSessionId) return;
      try {
        const status = await api.wizardDnsStatus(wizardSessionId);
        setDnsStatus(status);
        if (status.onCloudflare) {
          setAutomationLog(prev => [...prev, '✓ Nameservers pointed to Cloudflare — DNS automation enabled']);
          markStep('ns-check', 'complete');
          advanceStep();
          if (intervalRef.current) clearInterval(intervalRef.current);
        }
      } catch { /* ignore */ }
    }, [wizardSessionId]);

    const startPolling = () => {
      setPolling(true);
      pollDns();
      intervalRef.current = setInterval(pollDns, 30000);
    };

    useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);

    return (
      <div className="wizard-step-content">
        <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 600 }}>Nameserver Check</h2>
        <p style={{ color: 'var(--text-dim)', fontSize: 13, margin: '0 0 16px' }}>
          Your domain's nameservers are not yet pointed to Cloudflare. DNS automation requires Cloudflare nameservers.
        </p>
        {dnsStatus && (
          <div className="wizard-info-box">
            <strong>Current nameservers:</strong>
            <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
              {dnsStatus.nameservers.map((ns, i) => <li key={i} style={{ fontSize: 13 }}>{ns}</li>)}
            </ul>
          </div>
        )}
        <div className="wizard-info-box" style={{ background: 'var(--surface-2)', marginTop: 12 }}>
          <strong>IONOS Instructions:</strong>
          <ol style={{ margin: '8px 0', paddingLeft: 20, fontSize: 13, lineHeight: 1.6 }}>
            <li>Log in to your IONOS control panel</li>
            <li>Go to <strong>Domains &amp; SSL</strong> → select your domain</li>
            <li>Click <strong>DNS Settings</strong> → <strong>Nameservers</strong></li>
            <li>Select <strong>Use custom nameservers</strong></li>
            <li>Enter your Cloudflare nameservers (found in Cloudflare dashboard)</li>
            <li>Save changes — propagation takes 1-48 hours</li>
          </ol>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="wizard-btn primary" onClick={startPolling} disabled={polling}>
            {polling ? 'Checking every 30s…' : 'Start Checking'}
          </button>
          <button className="wizard-btn secondary" onClick={() => { markStep('ns-check', 'skipped'); advanceStep(); }}>
            Skip — I'll set up DNS later
          </button>
        </div>
      </div>
    );
  };

  // ─── Step 0: What Are You Building? ───────────────────────
  // Lifted parsed spec state — shared between ProjectStep and SpecStep
  const [liftedParsedSpec, setLiftedParsedSpec] = useState<ParsedSpec | null>(null);

  const SpecStep = () => {
    type SpecPath = 'choose' | 'upload' | 'generate' | 'skip';
    const [path, setPath] = useState<SpecPath>('choose');
    const [answers, setAnswers] = useState(() => {
      // Pre-fill from parsed spec if available (user uploaded a doc then switched to questions)
      const p = liftedParsedSpec?.parsed;
      return {
        problemStatement: p?.problemStatement || p?.description || '',
        targetUsers: p?.targetUsers || '',
        coreFeatures: p?.coreFeatures || (p?.keyFeatures || []).join(', ') || '',
        techPreferences: p?.techPreferences || p?.techStack || '',
        successCriteria: p?.successCriteria || '',
      };
    });
    const [streaming, setStreaming] = useState(false);
    const [streamedText, setStreamedText] = useState('');
    const [specComplete, setSpecComplete] = useState(false);
    const [specFile, setSpecFile] = useState<File | null>(null);
    const [parsing, setParsing] = useState(false);
    const [parseError, setParseError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const streamRef = useRef<HTMLDivElement>(null);
    const autoSavedRef = useRef(false);

    // Auto-scroll streaming output
    useEffect(() => {
      if (streamRef.current) streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }, [streamedText]);

    // Auto-save and skip if spec was already parsed during ProjectStep upload
    useEffect(() => {
      if (liftedParsedSpec && wizardSessionId && !autoSavedRef.current) {
        autoSavedRef.current = true;
        const p = liftedParsedSpec.parsed;
        const specContent = `# Project Specification\n\n${p.summary || ''}\n\n## Problem\n${p.problemStatement || p.description || ''}\n\n## Target Users\n${p.targetUsers || ''}\n\n## Core Features\n${(p.keyFeatures || []).map((f: string) => `- ${f}`).join('\n')}\n\n## Tech Stack\n${p.techPreferences || p.techStack || ''}\n\n## Success Criteria\n${p.successCriteria || ''}\n`;
        api.wizardSaveSpec(wizardSessionId, specContent).then(() => {
          setAutomationLog(prev => [...prev, `✓ SPEC.md auto-generated from uploaded document`]);
          markStep('spec', 'complete');
          advanceStep();
        }).catch(() => {
          // If save fails, fall through to normal SpecStep flow
        });
      }
    }, [liftedParsedSpec, wizardSessionId]);

    const handleUpload = async (file: File) => {
      setSpecFile(file);
      setParsing(true);
      setParseError(null);
      try {
        const result = await api.wizardParseSpec(file);
        setLiftedParsedSpec(result);
        setSpecComplete(true);
        setAutomationLog(prev => [...prev, `✓ Spec parsed: ${file.name}`]);
        // Auto-advance to ProjectStep — SPEC.md will be saved after project creation
        // (wizardSessionId is null at this point since project hasn't been created yet)
        markStep('spec', 'complete');
        advanceStep();
      } catch (err: unknown) {
        setParseError(err instanceof Error ? err.message : 'Failed to parse spec');
      } finally {
        setParsing(false);
      }
    };

    const handleGenerate = () => {
      if (!wizardSessionId) { setError('Start a project first'); return; }
      const allFilled = Object.values(answers).every(v => v.trim());
      if (!allFilled) { setError('Please answer all 5 questions'); return; }
      setStreaming(true);
      setStreamedText('');
      setError(null);

      const source = api.wizardGenerateSpec(wizardSessionId, answers) as unknown as EventTarget & { close: () => void };
      source.addEventListener('delta', ((e: CustomEvent) => {
        setStreamedText(prev => prev + (e.detail?.text || ''));
      }) as EventListener);
      source.addEventListener('complete', (() => {
        setStreaming(false);
        setSpecComplete(true);
      }) as EventListener);
      source.addEventListener('error', ((e: CustomEvent) => {
        setStreaming(false);
        if (e.detail?.fallback) {
          setSpecComplete(true); // Fallback placeholder was saved
          setAutomationLog(prev => [...prev, '⚠ SPEC.md generation failed — placeholder saved']);
        } else {
          setError(e.detail?.error || 'Spec generation failed');
        }
      }) as EventListener);
    };

    const handleContinue = () => {
      markStep('spec', specComplete ? 'complete' : 'skipped');
      advanceStep();
    };

    const handleSkip = () => {
      markStep('spec', 'skipped');
      advanceStep();
    };

    // Path chooser
    if (path === 'choose') {
      return (
        <div className="wizard-step-content">
          <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 600 }}>What Are You Building?</h2>
          <p style={{ color: 'var(--text-dim)', fontSize: 13, margin: '0 0 24px' }}>
            Help us understand your project. A good spec helps the AI pipeline make better decisions.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button className="wizard-btn secondary" onClick={() => setPath('upload')} style={{ textAlign: 'left', padding: '16px 20px' }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>📄 Upload a Spec Document</div>
              <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 4 }}>PDF, DOCX, or Markdown — AI will extract project details</div>
            </button>
            <button className="wizard-btn secondary" onClick={() => setPath('generate')} style={{ textAlign: 'left', padding: '16px 20px' }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>✨ Answer 5 Questions</div>
              <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 4 }}>Claude will generate a SPEC.md from your answers — live streaming</div>
            </button>
            <button className="wizard-btn secondary" onClick={handleSkip} style={{ textAlign: 'left', padding: '16px 20px', opacity: 0.7 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>⏭ Skip for Now</div>
              <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 4 }}>You can always add a SPEC.md later</div>
            </button>
          </div>
        </div>
      );
    }

    // Path A: Upload
    if (path === 'upload') {
      return (
        <div className="wizard-step-content">
          <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 600 }}>Upload Project Spec</h2>
          <p style={{ color: 'var(--text-dim)', fontSize: 13, margin: '0 0 16px' }}>
            Drop a PDF, DOCX, or Markdown file. AI will extract and save it as SPEC.md.
          </p>
          <input ref={fileInputRef} type="file" accept=".pdf,.docx,.md,.txt,.doc" style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); }} />
          <div onClick={() => fileInputRef.current?.click()} style={{
            border: `2px dashed ${specComplete ? 'var(--green)' : 'var(--border)'}`,
            borderRadius: 10, padding: '32px 24px', textAlign: 'center', cursor: 'pointer',
            background: specComplete ? 'rgba(0,200,100,0.04)' : 'var(--surface-2)',
          }}>
            {parsing ? (
              <SpecAnalysisProgress filename={specFile?.name} />
            ) : specComplete ? (
              <div><div style={{ fontSize: 20, marginBottom: 6 }}>✓</div>
                <div style={{ fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>Spec saved — {specFile?.name}</div></div>
            ) : (
              <div><div style={{ fontSize: 20, marginBottom: 6 }}>📄</div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Click to upload or drag & drop</div></div>
            )}
          </div>
          {parseError && <div className="wizard-error-box" style={{ marginTop: 12, fontSize: 12 }}>{parseError}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="wizard-btn secondary" onClick={() => setPath('choose')}>← Back</button>
            <button className="wizard-btn primary" onClick={handleContinue} disabled={!specComplete}>Continue</button>
          </div>
        </div>
      );
    }

    // Path B: Generate from questions
    if (path === 'generate') {
      const questions = [
        { key: 'problemStatement', label: 'What problem does this solve?', placeholder: 'Describe the core problem your project addresses…' },
        { key: 'targetUsers', label: 'Who are the target users?', placeholder: 'Technical founders, enterprise teams, consumers…' },
        { key: 'coreFeatures', label: 'What are the core features?', placeholder: 'List 3-5 key features or capabilities…' },
        { key: 'techPreferences', label: 'Any tech stack preferences?', placeholder: 'React + Node.js, Python + FastAPI, etc. (or leave blank)' },
        { key: 'successCriteria', label: 'How will you measure success?', placeholder: 'KPIs, metrics, milestones…' },
      ];

      return (
        <div className="wizard-step-content">
          <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 600 }}>Describe Your Project</h2>
          <p style={{ color: 'var(--text-dim)', fontSize: 13, margin: '0 0 16px' }}>
            Answer 5 questions and Claude will generate a comprehensive SPEC.md.
          </p>

          {!streaming && !specComplete && (
            <>
              {questions.map(q => (
                <div key={q.key} className="wizard-field" style={{ marginBottom: 12 }}>
                  <label style={{ fontWeight: 600, fontSize: 13 }}>{q.label}</label>
                  <textarea
                    value={answers[q.key as keyof typeof answers]}
                    onChange={e => setAnswers(prev => ({ ...prev, [q.key]: e.target.value }))}
                    placeholder={q.placeholder}
                    rows={2}
                    style={{ width: '100%', padding: '8px 12px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }}
                  />
                </div>
              ))}
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button className="wizard-btn secondary" onClick={() => setPath('choose')}>← Back</button>
                <button className="wizard-btn primary" onClick={handleGenerate}
                  disabled={!Object.values(answers).every(v => v.trim())}>
                  ✨ Generate SPEC.md
                </button>
              </div>
            </>
          )}

          {(streaming || specComplete) && (
            <>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, color: streaming ? 'var(--orange)' : 'var(--green)', marginBottom: 8 }}>
                {streaming ? '⚡ Claude is writing…' : '✓ SPEC.md generated'}
              </div>
              <div ref={streamRef} style={{
                background: 'var(--surface-2)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '16px', maxHeight: 320, overflow: 'auto',
                fontSize: 12, lineHeight: 1.7, fontFamily: 'var(--font-mono)', whiteSpace: 'pre-wrap',
                color: 'var(--text-dim)',
              }}>
                {streamedText || 'Waiting for response…'}
              </div>
              {specComplete && (
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <button className="wizard-btn primary" onClick={handleContinue}>Continue →</button>
                </div>
              )}
            </>
          )}
        </div>
      );
    }

    return null;
  };

  // ─── Launch Step ──────────────────────────────────────────
  const LaunchStep = () => {
    const completedServices = STEPS.filter(s => stepStatuses[s.id] === 'complete' && s.id !== 'project' && s.id !== 'ns-check' && s.id !== 'launch' && s.id !== 'spec');
    return (
      <div className="wizard-step-content">
        <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 600 }}>Ready to Launch</h2>
        <p style={{ color: 'var(--text-dim)', fontSize: 13, margin: '0 0 16px' }}>
          {completedServices.length} services connected. Your project is ready.
        </p>
        <div className="wizard-info-box">
          <strong>Connected Services:</strong>
          <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
            {completedServices.map(s => <li key={s.id} style={{ fontSize: 13 }}>{s.label}</li>)}
          </ul>
        </div>
        {codexStatus === 'running' && (
          <div className="wizard-info-box" style={{ background: 'var(--accent-bg)', marginTop: 12 }}>
            ⚡ Codex is scaffolding your repo…
          </div>
        )}
        {codexStatus === 'complete' && (
          <div className="wizard-success-box" style={{ marginTop: 12 }}>
            ✓ Repo scaffolded successfully
          </div>
        )}
        <button className="wizard-btn primary" style={{ marginTop: 16 }} onClick={() => {
          markStep('launch', 'complete');
          sessionStorage.removeItem('wizard_session');
          onComplete?.();
        }}>
          Go to Dashboard
        </button>
      </div>
    );
  };

  // ─── Step Renderer ────────────────────────────────────────
  const sharedProps = {
    wizardSessionId, stepStatuses, projectId,
    markStep, advanceStep,
    setAutomationLog, setCapabilities, setCodexStatus, setError,
  };

  const renderStep = () => {
    const step = STEPS[currentStep];
    if (!step) return null;

    switch (step.id) {
      case 'spec': return <SpecStep />;
      case 'project': return <ProjectStep />;
      case 'ns-check': return <NsCheckStep />;
      case 'github': return (
        <ServiceStep key="github" service="github" helpText="Enter a GitHub Personal Access Token with repo scope."
          fields={[{ key: 'token', label: 'Personal Access Token', placeholder: 'ghp_xxxxxxxxxxxx', type: 'password' }]}
          {...sharedProps} />
      );
      case 'supabase': return (
        <ServiceStep key="supabase" service="supabase" helpText="Enter your Supabase project URL and service role key."
          fields={[
            { key: 'url', label: 'Project URL', placeholder: 'https://xxxx.supabase.co' },
            { key: 'serviceRoleKey', label: 'Service Role Key', placeholder: 'eyJhbGciOi...', type: 'password' },
          ]}
          {...sharedProps} />
      );
      case 'cloudflare': return (
        <ServiceStep key="cloudflare" service="cloudflare" helpText="Cloudflare API token enables automatic DNS record creation for Railway, Netlify, and Mailgun."
          fields={[
            { key: 'token', label: 'API Token', placeholder: 'xxxxxxxxxxxx', type: 'password' },
            { key: 'zoneId', label: 'Zone ID (optional)', placeholder: 'Found in Cloudflare dashboard overview' },
          ]}
          {...sharedProps} />
      );
      case 'railway': return (
        <ServiceStep key="railway" service="railway" helpText="Railway API token for backend deployment."
          fields={[{ key: 'token', label: 'API Token', placeholder: 'xxxxxxxxxxxx', type: 'password' }]}
          {...sharedProps} />
      );
      case 'netlify': return (
        <ServiceStep key="netlify" service="netlify" helpText="Netlify personal access token for frontend deployment."
          fields={[{ key: 'token', label: 'Personal Access Token', placeholder: 'xxxxxxxxxxxx', type: 'password' }]}
          {...sharedProps} />
      );
      case 'mailgun': return (
        <ServiceStep key="mailgun" service="mailgun" helpText="Mailgun API key for transactional email."
          fields={[{ key: 'apiKey', label: 'API Key', placeholder: 'key-xxxxxxxxxxxx', type: 'password' }]}
          {...sharedProps} />
      );
      case 'stripe': return (
        <ServiceStep key="stripe" service="stripe" helpText="Stripe secret key for billing integration."
          fields={[{ key: 'secretKey', label: 'Secret Key', placeholder: 'sk_test_xxxxxxxxxxxx', type: 'password' }]}
          {...sharedProps} />
      );
      case 'openai': return (
        <ServiceStep key="openai" service="openai" helpText="OpenAI API key enables AI pipeline execution via Codex. When added to a claude+human project, OCNexus can offer to upgrade to claude+codex automatically."
          fields={[{ key: 'apiKey', label: 'API Key', placeholder: 'sk-xxxxxxxxxxxx', type: 'password' }]}
          {...sharedProps} />
      );
      case 'deploy-verify': return (
        <DeployVerifyStep
          projectId={projectId || ''}
          onComplete={() => { markStep('deploy-verify', 'complete'); advanceStep(); }}
          onSkip={() => { markStep('deploy-verify', 'skipped'); advanceStep(); }}
        />
      );
      case 'launch': return <LaunchStep />;
      default: return null;
    }
  };

  // ─── Render ───────────────────────────────────────────────
  // W-7: Handle Start Over — reset wizard via repair endpoint
  const handleStartOver = async () => {
    setShowResumePrompt(false);
    if (projectId) {
      try {
        await api.repairProject(projectId, 'reset');
      } catch { /* ignore — repair may not be available */ }
    }
    sessionStorage.removeItem('wizard_session');
    setCurrentStep(0);
    setStepStatuses(Object.fromEntries(STEPS.map((s, i) => [s.id, i === 0 ? 'active' : 'pending'])) as Record<StepId, StepStatus>);
    setWizardSessionId(null);
    setProjectId(null);
    setCapabilities([]);
    setAutomationLog([]);
    setCodexStatus('not_started');
    setDnsStatus(null);
    setError(null);
    // Reset V2 intake state
    setWizardPhase('intake');
    setIntakeAnswers(null);
  };

  // ─── Intake handler ──────────────────────────────────────
  const handleIntakeStart = useCallback((answers: IntakeAnswers) => {
    setIntakeAnswers(answers);
    setAutomationLog(prev => [...prev, `✓ Intake complete — ${answers.projectType} project, ${computeStepCount(answers)} steps`]);
    // Move to project setup phase (existing ProjectStep)
    setWizardPhase('project');
  }, []);

  // ─── WizardShell step renderer (maps WizardStep → existing ServiceStep / DeployConfigStep) ───
  const renderDynamicStep = useCallback((stepId: WizardStep): React.ReactNode => {
    // Map path-matrix WizardStep IDs to existing ServiceStep components
    switch (stepId) {
      case 'github':
        return (
          <ServiceStep key="github" service="github" helpText="Enter a GitHub Personal Access Token with repo scope."
            fields={[{ key: 'token', label: 'Personal Access Token', placeholder: 'ghp_xxxxxxxxxxxx', type: 'password' }]}
            {...sharedProps} />
        );
      case 'supabase':
        return (
          <ServiceStep key="supabase" service="supabase" helpText="Enter your Supabase project URL and service role key."
            fields={[
              { key: 'url', label: 'Project URL', placeholder: 'https://xxxx.supabase.co' },
              { key: 'serviceRoleKey', label: 'Service Role Key', placeholder: 'eyJhbGciOi...', type: 'password' },
            ]}
            {...sharedProps} />
        );
      case 'railway':
        return (
          <ServiceStep key="railway" service="railway" helpText="Railway API token for backend deployment."
            fields={[{ key: 'token', label: 'API Token', placeholder: 'xxxxxxxxxxxx', type: 'password' }]}
            {...sharedProps} />
        );
      case 'deploy_config':
        return (
          <DeployConfigStep
            key="deploy_config"
            deployTarget={(intakeAnswers?.deployTarget ?? 'unsure') as DeployTarget}
            onComplete={() => { markStep('railway' as StepId, 'complete'); advanceStep(); }}
            onSkip={() => { markStep('railway' as StepId, 'skipped'); advanceStep(); }}
          />
        );
      case 'netlify':
        return (
          <ServiceStep key="netlify" service="netlify" helpText="Netlify personal access token for frontend deployment."
            fields={[{ key: 'token', label: 'Personal Access Token', placeholder: 'xxxxxxxxxxxx', type: 'password' }]}
            {...sharedProps} />
        );
      case 'mailgun':
        return (
          <ServiceStep key="mailgun" service="mailgun" helpText="Mailgun API key for transactional email."
            fields={[{ key: 'apiKey', label: 'API Key', placeholder: 'key-xxxxxxxxxxxx', type: 'password' }]}
            {...sharedProps} />
        );
      case 'stripe':
        return (
          <ServiceStep key="stripe" service="stripe" helpText="Stripe secret key for billing integration."
            fields={[{ key: 'secretKey', label: 'Secret Key', placeholder: 'sk_test_xxxxxxxxxxxx', type: 'password' }]}
            {...sharedProps} />
        );
      case 'deploy_verify':
        return (
          <DeployVerifyStep
            projectId={projectId || ''}
            onComplete={() => { markStep('deploy-verify', 'complete'); advanceStep(); }}
            onSkip={() => { markStep('deploy-verify', 'skipped'); advanceStep(); }}
          />
        );
      default:
        return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardSessionId, stepStatuses, projectId, intakeAnswers]);

  // ─── Phase 1: Intake Screen (full-page, no sidebar) ─────────────
  if (wizardPhase === 'intake') {
    return (
      <IntakeScreen
        onStart={handleIntakeStart}
        computeStepCount={computeStepCount}
        computeStepNames={computeStepNames}
      />
    );
  }

  // ─── Phase 2: Project Setup (existing ProjectStep inside wizard layout) ───
  if (wizardPhase === 'project') {
    return (
      <>
        {/* W-7: Visual resume prompt */}
        {showResumePrompt && (
          <ResumePrompt
            projectName={resumeProjectName}
            steps={STEPS.map(s => ({ id: s.id, label: s.label, status: stepStatuses[s.id] }))}
            currentStepIndex={currentStep}
            totalSteps={STEPS.length}
            onContinue={() => setShowResumePrompt(false)}
            onStartOver={handleStartOver}
          />
        )}
        <div className="wizard-container">
          <div className="wizard-main">
            {/* Minimal progress: show intake summary + project step */}
            <div style={{ marginBottom: 16, padding: '10px 16px', background: 'var(--surface-2)', borderRadius: 8, fontSize: 12, color: 'var(--text-dim)' }}>
              Project type: <strong style={{ color: 'var(--text)' }}>{intakeAnswers?.projectType ?? 'unknown'}</strong>
              {' — '}
              {wizardPath.length} setup step{wizardPath.length !== 1 ? 's' : ''} after project creation
            </div>

            {/* Error Banner */}
            {error && (
              <div className="wizard-error-box" style={{ margin: '0 0 16px' }}>
                {error}
                <button onClick={() => setError(null)} style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>✕</button>
              </div>
            )}

            {/* Render the existing ProjectStep (with SpecStep first if needed) */}
            {renderStep()}
          </div>

          {/* Right: Capability Panel */}
          <div className="wizard-panel">
            <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Capabilities</h3>
            {capabilities.length > 0 ? (
              <div style={{ marginBottom: 20 }}>
                {capabilities.map((cap, i) => (
                  <div key={i} className="wizard-capability-item">
                    <span style={{ color: 'var(--green)' }}>●</span> {cap}
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: 'var(--text-dim)', fontSize: 13, margin: '0 0 20px' }}>
                Connect services to unlock capabilities.
              </p>
            )}
            <h4 style={{ margin: '16px 0 8px', fontSize: 13, fontWeight: 600, color: 'var(--text-dim)' }}>Automation Log</h4>
            <div ref={logRef} className="wizard-log">
              {automationLog.length === 0 ? (
                <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>No events yet.</span>
              ) : (
                automationLog.map((entry, i) => (
                  <div key={i} className="wizard-log-entry">{entry}</div>
                ))
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  // ─── Phase 3: Dynamic Wizard (WizardShell with computed path) ───────
  // Build shell step statuses record from the dynamic path
  const shellStepStatuses: Record<string, ShellStepStatus> = {};
  for (const stepId of wizardPath) {
    const legacyId = stepId === 'deploy_verify' ? 'deploy-verify' : stepId === 'deploy_config' ? 'railway' : stepId;
    shellStepStatuses[stepId] = ((legacyId in stepStatuses) ? stepStatuses[legacyId as StepId] : 'pending') as ShellStepStatus;
  }
  // Ensure the current step shows as 'active' if it's still pending
  if (wizardPath[currentStep] && shellStepStatuses[wizardPath[currentStep]] === 'pending') {
    shellStepStatuses[wizardPath[currentStep]] = 'active';
  }

  // Build right panel content
  const rightPanelContent = (
    <>
      <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Capabilities</h3>
      {capabilities.length > 0 ? (
        <div style={{ marginBottom: 20 }}>
          {capabilities.map((cap, i) => (
            <div key={i} className="wizard-capability-item">
              <span style={{ color: 'var(--green)' }}>●</span> {cap}
            </div>
          ))}
        </div>
      ) : (
        <p style={{ color: 'var(--text-dim)', fontSize: 13, margin: '0 0 20px' }}>
          Connect services to unlock capabilities.
        </p>
      )}
      {codexStatus !== 'not_started' && (
        <div className="wizard-codex-status" data-status={codexStatus}>
          <strong>Codex Scaffold:</strong>{' '}
          {codexStatus === 'running' && '⚡ Running…'}
          {codexStatus === 'complete' && '✓ Complete'}
          {codexStatus === 'failed' && '✗ Failed'}
        </div>
      )}
      <h4 style={{ margin: '16px 0 8px', fontSize: 13, fontWeight: 600, color: 'var(--text-dim)' }}>Automation Log</h4>
      <div ref={logRef} className="wizard-log">
        {automationLog.length === 0 ? (
          <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>No events yet.</span>
        ) : (
          automationLog.map((entry, i) => (
            <div key={i} className="wizard-log-entry">{entry}</div>
          ))
        )}
      </div>
    </>
  );

  return (
    <>
      {/* W-7: Visual resume prompt */}
      {showResumePrompt && (
        <ResumePrompt
          projectName={resumeProjectName}
          steps={wizardPath.map(s => ({ id: s, label: STEP_LABELS[s], status: shellStepStatuses[s] }))}
          currentStepIndex={currentStep}
          totalSteps={wizardPath.length}
          onContinue={() => setShowResumePrompt(false)}
          onStartOver={handleStartOver}
        />
      )}
      <WizardShell
        path={wizardPath}
        currentStepIndex={currentStep}
        stepStatuses={shellStepStatuses}
        renderStep={renderDynamicStep}
        onNavigate={(i) => setCurrentStep(i)}
        error={error}
        onClearError={() => setError(null)}
        rightPanel={rightPanelContent}
      />
    </>
  );
}
